import { NavLink } from "react-router-dom";
import { Building2, LogOut, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";


export default function AppShell({ title, subtitle, navItems, children, user, tenant, onLogout, actions }) {
  return (
    <div className="min-h-screen overflow-x-hidden bg-[#FAF8F3] text-[#0D1B2A] lg:h-screen lg:overflow-hidden">
      <div className="mx-auto grid min-h-screen max-w-[1600px] grid-cols-1 gap-6 overflow-x-hidden px-4 py-4 lg:h-full lg:min-h-0 lg:grid-cols-[270px_minmax(0,1fr)] lg:px-6 lg:py-6">
        <aside className="flex flex-col gap-6 rounded-[28px] border border-[#0D1B2A]/10 bg-[#0D1B2A] p-5 text-white shadow-[0_24px_60px_rgba(13,27,42,0.14)] lg:h-full lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto" data-testid="app-sidebar-panel">
          <div className="space-y-4">
            <div className="flex items-center gap-3" data-testid="app-brand-block">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F4A300] text-[#0D1B2A] shadow-lg">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#F4A300]" data-testid="brand-caption">Structured people operations</p>
                <h1 className="text-xl font-extrabold text-white" data-testid="brand-title">Tarka HR</h1>
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-4 text-white" data-testid="session-user-card">
              <p className="text-xs uppercase tracking-[0.24em] text-white/60" data-testid="session-role-label">{user?.role?.replaceAll("_", " ")}</p>
              <p className="mt-2 text-lg font-bold" data-testid="session-user-name">{user?.name}</p>
              <div className="mt-3 flex items-center gap-2 text-sm text-white/70" data-testid="session-tenant-row">
                <Building2 className="h-4 w-4" />
                <span data-testid="session-tenant-name">{tenant?.name || "Platform Workspace"}</span>
              </div>
            </div>
          </div>

          <nav className="flex flex-1 flex-col gap-2" data-testid="app-nav-list">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-link-${item.testId}`}
                className={({ isActive }) =>
                  `group flex items-center justify-between rounded-2xl border px-4 py-3 text-sm font-semibold transition duration-200 ${
                    isActive
                      ? "border-[#F4A300]/25 bg-[#F4A300]/12 text-[#F4A300] shadow-sm"
                      : "border-transparent bg-transparent text-white/72 hover:border-white/10 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <span>{item.label}</span>
                <span className="text-[11px] uppercase tracking-[0.24em] text-white/35">{item.shortLabel}</span>
              </NavLink>
            ))}
          </nav>

          <Button
            type="button"
            variant="outline"
            className="w-full justify-start rounded-2xl border-white/12 bg-white/5 py-6 text-white hover:bg-white/10 hover:text-white"
            onClick={onLogout}
            data-testid="logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </aside>

        <main className="min-w-0 space-y-6 lg:h-full lg:overflow-y-auto lg:pr-2" data-testid="app-main-content-scroll-region">
          <header className="min-w-0 rounded-[28px] border border-[#0D1B2A]/10 bg-white p-6 shadow-[0_18px_40px_rgba(13,27,42,0.06)]" data-testid="page-header-panel">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div className="max-w-3xl space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C27D00]" data-testid="page-subtitle-label">Tarka HR</p>
                <h2 className="text-4xl font-extrabold tracking-tight text-[#0D1B2A] sm:text-5xl" data-testid="page-title">{title}</h2>
                <p className="max-w-2xl text-sm text-[#0D1B2A]/62 sm:text-base" data-testid="page-subtitle">{subtitle}</p>
              </div>
              {actions ? <div data-testid="page-header-actions">{actions}</div> : null}
            </div>
          </header>

          <div className="min-w-0 grid gap-6" data-testid="page-content-region">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}